import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { formatDate } from '../../utils/dateFormatter';
import { canWrite } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRANSACTION_TYPES = [
  'General Expenses',
  'Salaries',
  'Miscellaneous',
  'Office Supplies',
  'Purchases',
  'Finance'
];

export const GeneralLedger = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    transaction_type: 'General Expenses',
    description: '',
    debit: 0,
    credit: 0
  });

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/ledger`, { headers: getAuthHeader() });
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch ledger entries');
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/ledger`, formData, { headers: getAuthHeader() });
      toast.success('Entry added successfully');
      setIsOpen(false);
      fetchEntries();
      setFormData({
        date: new Date().toISOString().split('T')[0],
        transaction_type: 'General Expenses',
        description: '',
        debit: 0,
        credit: 0
      });
    } catch (error) {
      toast.error('Failed to add entry');
    }
  };

  const handleDelete = async (date, description) => {
    if (user.role !== 'admin') {
      toast.error('Only admin can delete');
      return;
    }
    try {
      await axios.delete(`${API}/ledger/${date}/${description}`, { headers: getAuthHeader() });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>Cash Book</h2>
        {canWrite(user?.role) && <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-accent hover:bg-accent/90" data-testid="add-ledger-btn">
              <Plus className="w-4 h-4" />
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('add')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>Add Cash Book Entry</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required data-testid="ledger-date-input" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('transactionType')}</Label>
                <select
                  value={formData.transaction_type}
                  onChange={(e) => setFormData({...formData, transaction_type: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                  data-testid="ledger-type-select"
                >
                  {TRANSACTION_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required data-testid="ledger-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>Debit</Label>
                  <Input type="number" step="0.01" value={formData.debit} onChange={(e) => setFormData({...formData, debit: parseFloat(e.target.value)})} data-testid="ledger-debit-input" />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>Credit</Label>
                  <Input type="number" step="0.01" value={formData.credit} onChange={(e) => setFormData({...formData, credit: parseFloat(e.target.value)})} data-testid="ledger-credit-input" />
                </div>
              </div>
              <div className="bg-muted/30 p-3 rounded-md text-sm">
                <p className="text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>Balance will be calculated automatically</p>
                <p className="text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>Debit adds to balance, Credit deducts from balance</p>
              </div>
              <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="ledger-submit-btn">
                <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
              </Button>
            </form>
          </DialogContent>
        </Dialog>}
      </div>

      <div className="flex gap-4 mb-4 items-end">
        <div>
          <Label className="text-xs text-muted-foreground">From</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" />
        </div>
        {(fromDate || toDate) && (
          <Button variant="ghost" size="sm" onClick={() => { setFromDate(''); setToDate(''); }} className="text-muted-foreground">
            Clear
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/50">
            <tr>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('type')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('description')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('debit')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('credit')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('balance')}</th>
              {user?.role === 'admin' && <th className="border border-border p-3 text-center font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {entries.filter(e => (!fromDate || e.date >= fromDate) && (!toDate || e.date <= toDate)).map((entry, idx) => (
              <tr key={idx} className="hover:bg-muted/30" data-testid={`ledger-row-${idx}`}>
                <td className="border border-border p-3">{formatDate(entry.date)}</td>
                <td className="border border-border p-3">{entry.transaction_type}</td>
                <td className="border border-border p-3">{entry.description}</td>
                <td className="border border-border p-3 text-end font-mono-numeric text-success">{entry.debit.toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric text-destructive">{entry.credit.toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric font-semibold">{entry.balance.toFixed(2)}</td>
                {user?.role === 'admin' && (
                  <td className="border border-border p-3 text-center">
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.date, entry.description)} data-testid={`delete-ledger-${idx}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {entries.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-ledger">
            <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noEntriesYet')}</p>
          </div>
        )}
      </div>
    </Card>
  );
};